import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import Footer from './components/Footer';
import FeatureGrids from './components/FeatureGrids';
import ReviewCollectionPage from './pages/ReviewCollectionPage';
import BottomNav from './components/BottomNav';
import SplashScreen from './components/SplashScreen';
import MyPage from './pages/MyPage';
import ChatPage from './pages/ChatPage';
import FortunePage from './pages/FortunePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RelationshipPage from './pages/RelationshipPage';
import GoldPage from './pages/GoldPage';
import DeepReportLandingPage from './pages/DeepReportLandingPage';
import { useSubscription } from './hooks/useSubscription';
import { useCredits } from './hooks/useCredits';

import PremiumBanner from './components/PremiumBanner';
import PricingPage from './pages/PricingPage';
import UsageHistoryPage from './pages/UsageHistoryPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFail from './pages/PaymentFail';
import SupportPage from './pages/SupportPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import PaymentManagement from './pages/admin/PaymentManagement';
import PlanManagement from './pages/admin/PlanManagement';
import AdminReviews from './pages/admin/AdminReviews';
import AdminDeepReports from './pages/admin/AdminDeepReports';
import { useAuth } from './hooks/useAuth';
import { useModalStore } from './hooks/useModalStore';
import { useInactivityLogout } from './hooks/useInactivityLogout';
import AuthCallback from './components/auth/AuthCallback';
// Lazy load modals for better initial performance
import FreePreview from './components/FreePreview';
import ResumeCheckoutBanner from './components/ResumeCheckoutBanner';
import { track, trackOnce } from './utils/analytics';
const AnalysisModal = lazy(() => import('./components/AnalysisModal'));
const MbtiSajuModal = lazy(() => import('./components/MbtiSajuModal'));
const DeepReportModal = lazy(() => import('./components/DeepReportModal'));
const RecommendationModal = lazy(() => import('./components/RecommendationModal'));
const CompatibilityModal = lazy(() => import('./components/CompatibilityModal'));
const CreditPurchaseModal = lazy(() => import('./components/CreditPurchaseModal'));
const AdminInquiries = lazy(() => import('./pages/admin/AdminInquiries'));
const AdminConsultations = lazy(() => import('./pages/admin/AdminConsultations'));
const OnboardingModal = lazy(() => import('./components/OnboardingModal'));
const DeepReportEventModal = lazy(() => import('./components/DeepReportEventModal'));

const ReviewsSection = lazy(() => import('./components/ReviewsSection'));
const PopupModal = lazy(() => import('./components/PopupModal'));
const CompatibilitySharePage = lazy(() => import('./pages/CompatibilitySharePage'));

// Consolidated fortune page
const MyLuckPage = lazy(() => import('./pages/MyLuckPage'));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage'));

const ShopPage = lazy(() => import('./pages/ShopPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const AdminShopProducts = lazy(() => import('./pages/admin/AdminShopProducts'));
const AdminShopOrders = lazy(() => import('./pages/admin/AdminShopOrders'));

function App() {
  const [showSplash, setShowSplash] = useState(false); // Disable splash screen by default for faster access
  const { session, loading: isAuthLoading } = useAuth();
  


  // Apply inactivity logout
  useInactivityLogout(session);

  const { modals, openModal, closeModal, closeAllModals } = useModalStore();




  const { tier } = useSubscription(session);
  const creditHook = useCredits(session);
  const { credits, refreshCredits, purchaseCredits, useCredits: consumeCredits, loading: isCreditsLoading } = creditHook;
  const [showPremiumBanner, setShowPremiumBanner] = useState(true);

  // Unified loading state for Safari (Session + Credits initial fetch)
  const isAppInitializing = isAuthLoading || (isCreditsLoading && !!session);

  // Check if profile needs completion
  useEffect(() => {
    if (session?.user && !isAuthLoading) {
      const meta = session.user.user_metadata;
      // 가입 마찰을 줄이기 위해 필수는 생년월일·성별만. MBTI/출생시간은 이후 선택 입력.
      const needsProfile = !meta?.birth_date || !meta?.gender;

      if (needsProfile) {
        // Prevent infinite loop by checking if the modal is already open in the correct mode
        if (modals?.analysis?.mode !== 'complete_profile' || !modals?.analysis?.isOpen) {
          openModal('analysis', 'complete_profile');
        }
      }
      // 온보딩 모달은 더 이상 자동으로 띄우지 않는다. 첫 결과 화면(MyLuckPage)의 웰컴 배너가 대체.
    }
  }, [session, isAuthLoading, openModal, modals?.analysis?.mode, modals?.analysis?.isOpen]);

  // Handle login trigger from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === 'true' && !session && !isAuthLoading) {
      openModal('analysis', 'login');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // 심층 리포트 구매 후 비회원이 가입하고 돌아왔을 때 이벤트 모달 자동 노출
    if (params.get('event_trigger') === 'true' && session && !isAuthLoading) {
        openModal('deepReportEvent');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [session, isAuthLoading, openModal]);


  if (showSplash) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  // 로딩 화면 표시 로직을 BrowserRouter 내부로 이동하여 /auth/callback 경로가 차단되지 않도록 함
  return (
    <BrowserRouter>
      <AppContent 
        session={session} 
        isAuthLoading={isAppInitializing}
        credits={credits}
        refreshCredits={refreshCredits}
        purchaseCredits={purchaseCredits}
        consumeCredits={consumeCredits}
        checkSufficientCredits={creditHook.checkSufficientCredits}
        getCost={creditHook.getCost}
        debugInfo={creditHook.debugInfo}
        tier={tier}

        showPremiumBanner={showPremiumBanner}
        setShowPremiumBanner={setShowPremiumBanner}
        modals={modals}
        closeModal={closeModal}
        closeAllModals={closeAllModals}
        openModal={openModal}
      />
    </BrowserRouter>
  );
}

function AppContent({ 
  session, isAuthLoading, credits, refreshCredits, purchaseCredits, consumeCredits, 
  checkSufficientCredits, getCost,
  tier, 
  showPremiumBanner, setShowPremiumBanner, modals, closeModal, closeAllModals, openModal 
}: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const isChatPage = location.pathname.startsWith('/chat') || location.pathname.startsWith('/room');
  const isAdminPage = location.pathname.startsWith('/admin');

  const handleStart = () => {
    if (session) navigate('/myluck?type=today');
    else openModal('analysis', 'signup');
  };

  // Handle automatic modal opening from share link
  useEffect(() => {
    if (location.state?.openCompatibility && session) {
      navigate('/relationship');
    }
  }, [location.state, session, navigate]);

  useEffect(() => {
    if (location.pathname === '/') trackOnce('landing_view');
  }, [location.pathname]);

  // 라우트 변경 시 모든 모달 닫기 (타로 등 모달이 열린 채로 다른 화면 이동 방지)
  useEffect(() => {
    closeAllModals();
  }, [location.pathname, closeAllModals]);

  const handleCloseOnboarding = () => {
    if (session?.user) {
      localStorage.setItem(`hasSeenOnboarding_${session.user.id}`, 'true');
    }
    closeModal('onboarding');
  };

  return (
    <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-white/50 backdrop-blur-sm z-[1000]">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
        </div>
      }>
        <Routes>
          {/* 인증 콜백 경로는 로딩 상태와 무관하게 즉시 렌더링하여 데드락 방지 */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Admin Login - No Sidebar/Header/Footer */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* Admin System - With Sidebar, No Header/Footer */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="deep-reports" element={<AdminDeepReports />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="payments" element={<PaymentManagement />} />
            <Route path="inquiries" element={<AdminInquiries />} />
            <Route path="consultations" element={<AdminConsultations />} />
            <Route path="plans" element={<PlanManagement />} />
            <Route path="shop/products" element={<AdminShopProducts />} />
            <Route path="shop/orders" element={<AdminShopOrders />} />
          </Route>

          {/* 나머지 모든 경로는 로딩 상태에 따라 분기 */}
          <Route path="*" element={
            <div className="selection:bg-indigo-100 selection:text-indigo-900 overflow-x-hidden pb-20 md:pb-0">
              <Navbar />

              {isAuthLoading && !session && location.pathname !== '/premium' ? (
                <div className="min-h-[70vh] flex flex-col items-center justify-center animate-fade-in p-6 text-center">
                  <div className="relative mb-6">
                    <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
                    <div className="absolute top-0 left-0 w-16 h-16 border-4 border-t-indigo-600 rounded-full animate-spin"></div>
                  </div>
                  <p className="text-slate-500 font-bold tracking-tight mb-4">당신의 운명을 불러오는 중...</p>
                  
                </div>
              ) : (
                <Routes>
                  <Route path="/" element={
                    <>
                      <HeroSection onStart={handleStart} user={session?.user} onOpenDeepReport={() => navigate('/premium')} />
                      {!session && <FreePreview onSignup={() => { track('signup_cta_click', { from: 'preview' }); openModal('analysis', 'signup'); }} />}
                      <FeatureGrids />


                      <ReviewsSection />
                      
                      {/* Featured Analysis Section */}
                      <section className="py-16 bg-[#faf8fe] relative">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>
                        <div className="section-container relative z-10">
                          <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold text-slate-900 mb-4">MBTI와 사주의 새로운 만남</h2>
                            <p className="text-slate-500 font-medium">단순한 재미를 넘어, 깊이 있는 자아 분석을 제공합니다.</p>
                          </div>

                          <div className="grid md:grid-cols-3 gap-8">
                            <button
                              onClick={() => {
                                navigate('/premium');
                              }}
                              className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-transform text-left group"
                            >
                              <div className="w-14 h-14 bg-violet-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-violet-100 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-brain w-7 h-7 text-violet-600"><path d="M9 18a4 4 0 0 1-2-3.82 4 4 0 0 1-1-2.18V7l3-4 4 4" /><path d="M15 18a4 4 0 0 0 2-3.82 4 4 0 0 0 1-2.18V7l-3-4-4 4" /><path d="M12 2c-1.8 0-3.6.48-5.11 1.34A6 6 0 0 0 12 18V2z" /><path d="M12 2a6 6 0 0 0 5.11 1.34A6 6 0 0 1 12 18V2z" /><path d="M12 18h0" /></svg>
                              </div>
                              <h3 className="text-xl font-bold mb-3 group-hover:text-violet-600 transition-colors">심도 있는 성격 매칭</h3>
                              <p className="text-sm text-slate-500 leading-relaxed font-medium mb-4">당신의 무의식과 타고난 운명이 어떻게 연결되어 있는지 과학적으로 분석합니다.</p>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-md">5,300+ 이용 중</span>
                                <span className="px-2 py-1 bg-amber-50 text-amber-600 text-[10px] font-bold rounded-md">인기</span>
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                navigate('/myluck?type=kbo');
                              }}
                              className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-transform text-left group"
                            >
                              <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors p-2">
                                <img
                                  src="/assets/icons/3d_kbo.png"
                                  alt="KBO"
                                  className="w-full h-full object-contain drop-shadow-lg"
                                />
                              </div>
                              <h3 className="text-xl font-bold mb-3 group-hover:text-blue-600 transition-colors">KBO 팬 궁합</h3>
                              <p className="text-sm text-slate-500 leading-relaxed font-medium mb-4">MBTI와 사주를 분석해 나와 가장 잘 맞는 KBO 프로야구 구단을 알려드립니다.</p>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-md">1,500+ 이용 중</span>
                                <span className="px-2 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-md">NEW</span>
                              </div>
                            </button>

                            <button
                              onClick={() => navigate('/relationship')}
                              className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-transform text-left group"
                            >
                              <div className="w-14 h-14 bg-pink-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-pink-100 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-users w-7 h-7 text-pink-600"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                              </div>
                              <h3 className="text-xl font-bold mb-3 group-hover:text-pink-600 transition-colors">인간관계 솔루션</h3>
                              <p className="text-sm text-slate-500 leading-relaxed font-medium mb-4">당신이 조심해야 할 운명과 시너지를 낼 수 있는 인연의 색깔을 알려드립니다.</p>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-pink-50 text-pink-600 text-[10px] font-bold rounded-md">3,300+ 이용 중</span>
                                <span className="px-2 py-1 bg-rose-50 text-rose-600 text-[10px] font-bold rounded-md">만족도 99%</span>
                              </div>
                            </button>
                          </div>
                        </div>
                      </section>
                    </>
                  } />
                  <Route path="/support" element={<SupportPage session={session} />} />
                  <Route path="/mypage" element={
                    <MyPage
                      onOpenDeepReport={(reportType) => openModal('deepReport', undefined, { reportType })}
                      onOpenMbtiSaju={() => openModal('mbtiSaju')}
                      onJamidusuClick={() => navigate('/myluck?type=jamidusu')}
                      onOpenCompatibility={() => navigate('/relationship')}
                      credits={credits}
                      refreshCredits={refreshCredits}
                      purchaseCredits={purchaseCredits}
                      spendCredits={consumeCredits}
                      checkSufficientCredits={checkSufficientCredits}
                      getCost={getCost}
                      session={session}
                    />
                  } />
                  <Route path="/reviews" element={<ReviewCollectionPage />} />
                  <Route path="/community" element={<ReviewCollectionPage />} />
                  <Route path="/fortune" element={
                    <FortunePage
                      onFortuneClick={() => navigate('/myluck?type=today')}
                      onMbtiSajuClick={() => {
                        navigate('/premium');
                      }}
                      onTarotClick={() => navigate('/myluck?type=tarot')}
                      onTripClick={() => navigate('/myluck?type=trip')}
                      onJamidusuClick={() => navigate('/myluck?type=jamidusu')}
                      onKboClick={() => navigate('/myluck?type=kbo')}
                      onCompatibilityClick={() => navigate('/relationship')}
                    />
                  } />
                  <Route path="/store" element={
                    <PricingPage
                      currentCredits={credits}
                      session={session}
                      onPurchaseSuccess={async (planId, pricePaid, creditAmount, paymentId) => {
                        await purchaseCredits(planId, pricePaid, creditAmount, paymentId);
                      }}
                    />
                  } />
                  <Route path="/pricing" element={
                    <PricingPage
                      currentCredits={credits}
                      session={session}
                      onPurchaseSuccess={async (planId, pricePaid, creditAmount, paymentId) => {
                        await purchaseCredits(planId, pricePaid, creditAmount, paymentId);
                      }}
                    />
                  } />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/shop/:productId" element={<ProductDetailPage />} />
                  <Route path="/shop/cart" element={<CartPage />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/fail" element={<PaymentFail />} />
                  <Route path="/usage-history" element={<UsageHistoryPage session={session} />} />
                  <Route path="/tarot" element={<ChatPage session={session} defaultService="tarot" />} />
                  <Route path="/saju" element={<ChatPage session={session} defaultService="saju" />} />
                  <Route path="/share/compatibility/:userId" element={<CompatibilitySharePage />} />
                  <Route path="/room" element={<ConsultationPage session={session} />} />
                  <Route path="/chat" element={<ConsultationPage session={session} />} />
                  <Route path="/relationship" element={<RelationshipPage session={session} />} />
                  <Route path="/gold" element={<GoldPage session={session} />} />
                  <Route path="/premium" element={<DeepReportLandingPage onOpenDeepReport={(reportType) => openModal('deepReport', undefined, { reportType })} />} />
                  <Route path="/myluck" element={<MyLuckPage session={session} />} />
                  <Route path="/today-fortune" element={<Navigate to="/myluck?type=today" replace />} />
                  <Route path="/today-tarot" element={<Navigate to="/myluck?type=tarot" replace />} />
                  <Route path="/trip" element={<Navigate to="/myluck?type=trip" replace />} />
                  <Route path="/jamidusu" element={<Navigate to="/myluck?type=jamidusu" replace />} />
                  <Route path="/kbo" element={<Navigate to="/myluck?type=kbo" replace />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                </Routes>
              )}

              {!isChatPage && <Footer />}
              {!isAdminPage && <BottomNav />}


              {/* Modals are now lazy loaded and managed by openModal store */}
              <AnalysisModal
                isOpen={modals?.analysis?.isOpen || false}
                onClose={() => closeModal('analysis')}
                mode={modals?.analysis?.mode}
                initialData={session?.user?.user_metadata}
              />

              <MbtiSajuModal
                isOpen={modals?.mbtiSaju?.isOpen || false}
                onClose={() => closeModal('mbtiSaju')}
                onNavigate={(service) => {
                  closeAllModals();
                  if (service === 'fortune') navigate('/myluck?type=today');
                  else openModal((service === 'mbti' ? 'deepReport' : service) as any);
                }}
                refreshCredits={refreshCredits}
                credits={credits}
                session={session}
                onOpenDeepReport={(reportType) => openModal('deepReport', undefined, { reportType })}
              />
              <DeepReportModal
                isOpen={modals?.deepReport?.isOpen || false}
                onClose={() => closeModal('deepReport')}
                session={session}
                initialReportType={modals?.deepReport?.data?.reportType}
              />

              <RecommendationModal
                isOpen={modals?.recommendation?.isOpen || false}
                onClose={() => closeModal('recommendation')}
                initialTab={modals?.recommendation?.mode}
              />
              <CompatibilityModal
                isOpen={modals?.compatibility?.isOpen || false}
                onClose={() => closeModal('compatibility')}
                onNavigate={(service: any) => {
                  closeAllModals();
                  if (service === 'fortune') navigate('/myluck?type=today');
                  else openModal((service === 'mbti' ? 'mbtiSaju' : service) as any);
                }}
                refreshCredits={refreshCredits}
                credits={credits}
                session={session}
              />

              <CreditPurchaseModal
                isOpen={modals?.creditPurchase?.isOpen || false}
                onClose={() => closeModal('creditPurchase')}
                userEmail={session?.user?.email}
                currentCredits={credits}
                requiredCredits={modals?.creditPurchase?.data?.requiredCredits}
                resumePlanId={modals?.creditPurchase?.data?.resumePlanId}
                onSuccess={async (planId, pricePaid, creditAmount, paymentId) => {
                  await purchaseCredits(planId, pricePaid, creditAmount, paymentId);
                }}
              />

              <OnboardingModal
                isOpen={modals?.onboarding?.isOpen || false}
                onClose={handleCloseOnboarding}
                onCheckPlans={() => openModal('creditPurchase')}
                userName={session?.user?.user_metadata?.full_name}
              />

              <DeepReportEventModal
                isOpen={modals?.deepReportEvent?.isOpen || false}
                onClose={() => closeModal('deepReportEvent')}
                session={session}
              />

              <PremiumBanner
                isVisible={showPremiumBanner}
                onClose={() => setShowPremiumBanner(false)}
                onCheckPlans={() => openModal('creditPurchase')}
                currentCredits={credits}
              />
              <ResumeCheckoutBanner
                enabled={!!session && !modals?.creditPurchase?.isOpen}
                onResume={(p) => openModal('creditPurchase', undefined, { resumePlanId: p.planId })}
              />
              <PopupModal />
            </div>
          } />
        </Routes>
      </Suspense>
  );
}

export default App;
